// 배송 관제의 「취소된 배송」 목록 — 그달 완료 취소된 기록(deliveryCancellations)을 최근 순으로: 누가·언제·누구 배송을·정정 문자 결과(대표 결정, 작은 결정 2건)
import { useEffect, useState } from "react";
import { onSnapshot, where } from "firebase/firestore";
import { orgQuery } from "@/lib/org";
import { fmtDateTime } from "@/lib/format";

interface Row {
  id: string;
  school?: string;
  studentName?: string;
  driverName?: string;
  canceledByName?: string;
  canceledAt?: { toDate?: () => Date };
  deliveredAt?: { toDate?: () => Date };
  correctionRequested?: boolean;
  correctionSent?: number;
  correctionFailed?: number;
  correctionReason?: string;
}

/** 정정 문자 결과 — 취소 함수가 적는 칸(correctionSent·correctionFailed·correctionReason)을 그대로 읽는다 */
function correctionText(r: Row): string {
  if (!r.correctionRequested) return "정정 문자 안 보냄(끔)";
  if ((r.correctionFailed || 0) > 0) return "정정 문자 보내기 실패"; // 원문 사유(영어 오류 등)는 화면에 바로 내지 않고 마우스를 올리면 보인다(title)
  if ((r.correctionSent || 0) > 0) return "정정 문자 보냄";
  if (r.correctionReason) return r.correctionReason; // 예: 처음 알림이 안 갔던 집
  return "정정 문자 결과 기록 없음";
}

export default function CanceledDeliveries({ yearMonth }: { yearMonth: string }) {
  const [rows, setRows] = useState<Row[]>([]);
  const [err, setErr] = useState("");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setErr("");
    const unsub = onSnapshot(
      orgQuery("deliveryCancellations", where("yearMonth", "==", yearMonth)),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Row, "id">) }));
        list.sort((a, b) => (b.canceledAt?.toDate?.()?.getTime() ?? 0) - (a.canceledAt?.toDate?.()?.getTime() ?? 0));
        setRows(list);
      },
      () => { setRows([]); setErr("취소된 배송 목록을 불러오지 못했습니다."); },
    );
    return () => unsub();
  }, [yearMonth]);

  if (!rows.length && !err) return null;
  return (
    <div className="card p-4 text-sm">
      <button className="w-full flex justify-between items-center min-h-[36px] font-bold" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
        <span>↩️ 취소된 배송 {rows.length}건 ({yearMonth})</span>
        <span className="text-slate-500 font-normal">{open ? "접기" : "보기"}</span>
      </button>
      {err && <div className="text-red-600 mt-1">{err}</div>}
      {open && (
        <ul className="mt-2 divide-y">
          {rows.map((r) => (
            <li key={r.id} className="py-2 space-y-0.5">
              <div><b>{r.school} {r.studentName}</b> <span className="text-slate-500">— 배송 {r.driverName || "-"}{r.deliveredAt?.toDate ? ` · ${fmtDateTime(r.deliveredAt.toDate())}` : ""}</span></div>
              <div className="text-slate-600">
                취소 {r.canceledByName || "-"}{r.canceledAt?.toDate ? ` · ${fmtDateTime(r.canceledAt.toDate())}` : ""} · <span title={(r.correctionFailed || 0) > 0 ? r.correctionReason || undefined : undefined}>{correctionText(r)}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs text-slate-500 mt-2">취소 사유는 아직 따로 받지 않습니다. 그달 청구서가 만들어지기 전(다음 달 1일 전)에 취소한 배송은 이용료 계산에서도 빠집니다.</p>
    </div>
  );
}
